import React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Notifications,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '@/app/store';
import { signOut } from '@/features/auth';
import { toggleSidebar } from '../uiSlice';
import { NotificationDropdown } from './NotificationDropdown';
import { ProfileDropdown } from './ProfileDropdown';
import { Notification } from '@/entities/ui';
import { getInitials } from '@/entities/user';
import { LogoMedium } from '../Logo';

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  showMenuButton = true,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { user } = useAppSelector(state => state.auth);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [notifications, setNotifications] = React.useState<Notification[]>([]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/app/profile');
  };

  const handleSettingsClick = () => {
    navigate('/app/profile');
  };

  const handleLogout = async () => {
    await queryClient.cancelQueries();
    await dispatch(signOut()).unwrap();
    queryClient.clear();
    navigate('/login');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification?.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const handleDeleteNotification = (id: string) => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== id)
    );
  };

  const handleMarkAllRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  const handleViewAllNotifications = () => {
    handleNotificationMenuClose();
  };

  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    } else {
      dispatch(toggleSidebar());
    }
  };

  const isMenuOpen = Boolean(anchorEl);

  return (
    <AppBar
      position="sticky"
      elevation={1}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: { xs: '40px', sm: '44px', md: '48px' },
          px: { xs: 0.75, sm: 1.5, md: 2 },
          py: 0,
        }}
      >
        {showMenuButton && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleMenuClick}
            size="small"
            sx={{
              mr: { xs: 0.5, sm: 1 },
              p: 0.25,
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        )}

        <LogoMedium logoUrl={user?.doohConfig?.logo || undefined} />
        <Box sx={{ flexGrow: 1 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          {!isSmallMobile && (
            <IconButton
              color="inherit"
              aria-label="notifications"
              size="small"
              onClick={handleNotificationMenuOpen}
              sx={{ p: 0.25 }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
          )}

          <IconButton
            size="small"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
            sx={{ p: 0.25 }}
          >
            <Avatar
              src={user?.photoURL || undefined}
              sx={{
                width: { xs: 24, sm: 28, md: 32 },
                height: { xs: 24, sm: 28, md: 32 },
                fontSize: { xs: '0.625rem', sm: '0.75rem', md: '0.875rem' },
              }}
            >
              {user?.displayName ? (
                getInitials(user.displayName)
              ) : (
                <AccountCircle />
              )}
            </Avatar>
          </IconButton>
        </Box>

        <ProfileDropdown
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleProfileMenuClose}
          user={user}
          onProfileClick={handleProfileClick}
          onSettingsClick={handleSettingsClick}
          onLogout={handleLogout}
          onNotificationClick={() => handleNotificationMenuOpen}
          unreadNotificationCount={unreadCount}
        />

        <NotificationDropdown
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationMenuClose}
          notifications={notifications}
          onMarkAsRead={handleMarkAsRead}
          onDelete={handleDeleteNotification}
          onMarkAllRead={handleMarkAllRead}
          onViewAll={handleViewAllNotifications}
        />
      </Toolbar>
    </AppBar>
  );
};
